import { useState } from "react";

import Box from "@mui/material/Box";
import { FieldValues, Path, useFormContext, useWatch } from "react-hook-form";

import { useSendAutomatedEmailTest } from "../hooks/useSendAutomatedEmailTest";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { trpc } from "@/common/helpers/trpc";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { Trans, useTranslate } from "@/common/hooks/useTranslate";
import { RouterInputs } from "@/common/types/trpc";
import { SendPreviewEmailDialog } from "@/components/SendPreviewEmailDialog/SendPreviewEmailDialog";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTooltip } from "@/dashboard-design-system/DashboardTooltip/DashboardTooltip";
import { useLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";
import { Send } from "@/icons/outlined";

type AutomatedEmailType = RouterInputs["sendAutomatedEmailTest"]["emailType"];

interface SendAutomatedEmailTestButtonProps<TFormData extends FieldValues> {
  emailType: AutomatedEmailType;
  subjectFieldName?: Path<TFormData>;
  contentFieldName?: Path<TFormData>;
  attachmentFieldName?: Path<TFormData>;
}

const EMAIL_TYPE_TITLE_KEYS = {
  "ticketing-thank-you": "emailEditor.sendTest.title.ticketingThankYou",
  "auction-thank-you-winner": "emailEditor.sendTest.title.auctionThankYouWinner",
  "auction-thank-you-non-winner": "emailEditor.sendTest.title.auctionThankYouNonWinner",
  "auction-outbid": "emailEditor.sendTest.title.auctionOutbid",
  "donation-thank-you": "emailEditor.sendTest.title.donationThankYou",
  "fundraiser-thank-you": "emailEditor.sendTest.title.fundraiserThankYou",
  "ticketing-reminder": "emailEditor.sendTest.title.ticketingReminder",
  "membership-renewal": "emailEditor.sendTest.title.membershipRenewal",
  "donation-renewal": "emailEditor.sendTest.title.donationRenewal",
  "fulfillment-ready-for-pickup": "emailEditor.sendTest.title.fulfillmentReadyForPickup",
  "fulfillment-order-shipped": "emailEditor.sendTest.title.fulfillmentOrderShipped",
} as const satisfies Record<AutomatedEmailType, string>;

export const SendAutomatedEmailTestButton = <TFormData extends FieldValues>({
  emailType,
  subjectFieldName,
  contentFieldName,
  attachmentFieldName,
}: SendAutomatedEmailTestButtonProps<TFormData>) => {
  const { t } = useTranslate();
  const { isSendingEmailForbidden, organization } = useCurrentUserContext();
  const { apiFormObject, editorMode, formCategory } = useLiveFormEditorContext();
  const form = useFormContext<TFormData>();
  const { sendTest, isSending } = useSendAutomatedEmailTest();
  const { logAmplitudeEvent } = useAmplitude();

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { data: activeOrganizationMembers, isLoading: activeOrganizationMembersLoading } =
    trpc.getActiveOrganizationMembers.useQuery(undefined, { enabled: isDialogOpen });

  const subject = useWatch({ control: form.control, name: subjectFieldName as Path<TFormData> });
  const content = useWatch({ control: form.control, name: contentFieldName as Path<TFormData> });

  const isMissingDraft = Boolean((subjectFieldName && !subject) || (contentFieldName && !content));
  const isDisabled = isMissingDraft || isSendingEmailForbidden;

  const tooltip =
    isDisabled && organization && !organization.paymentEnabledAtUtc
      ? t("dashboard", "emailEditor.mustValidateStripeAccountToSendEmails")
      : undefined;

  const handleOpen = async () => {
    logAmplitudeEvent(AmplitudeEvents.AutomatedEmailTestButtonClicked, {
      email_type: emailType,
      form_category: formCategory.toLowerCase(),
    });

    const fieldsToValidate = [subjectFieldName, contentFieldName].filter(Boolean) as Path<TFormData>[];
    if (fieldsToValidate.length > 0) {
      const isValid = await form.trigger(fieldsToValidate);
      if (!isValid) {
        return;
      }
    }
    setIsDialogOpen(true);
  };

  const handleSend = (recipientEmails: string[]) => {
    const attachment = attachmentFieldName
      ? (form.getValues(attachmentFieldName) as { fileUrl?: string | null } | null | undefined)
      : undefined;

    return sendTest({
      emailType,
      formId: apiFormObject.id,
      subject: subjectFieldName ? String(form.getValues(subjectFieldName) ?? "") : undefined,
      content: contentFieldName ? String(form.getValues(contentFieldName) ?? "") : undefined,
      attachmentUrl: attachment?.fileUrl ?? undefined,
      recipientEmails,
    });
  };

  if (editorMode !== "org") {
    return null;
  }

  return (
    <>
      <DashboardTooltip placement="bottom" title={tooltip}>
        <span>
          <DashboardButton
            vibe="neutral"
            variant="outlined"
            size="small"
            endIcon={<Send />}
            onClick={handleOpen}
            isLoading={isSending}
            disabled={isDisabled}
            data-test="send-automated-email-test-button"
          >
            {t("dashboard", "emailEditor.sendTestEmail")}
          </DashboardButton>
        </span>
      </DashboardTooltip>
      <SendPreviewEmailDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSend={handleSend}
        activeOrganizationMembers={activeOrganizationMembers}
        activeOrganizationMembersLoading={activeOrganizationMembersLoading}
        allowCustomEmail={false}
        title={
          <Trans
            space="dashboard"
            i18nKey={EMAIL_TYPE_TITLE_KEYS[emailType]}
            components={{ emphasis: <Box component="em" sx={{ fontStyle: "italic" }} /> }}
          />
        }
        description={t("dashboard", "emailEditor.sendTest.previewDescription")}
      />
    </>
  );
};
